import React, { useState, useEffect } from 'react'
import Button from '@material-ui/core/Button'
import Dialog from '@material-ui/core/Dialog'
import { TextField, Tooltip } from '@material-ui/core'
import MmsService from 'Services/MMS/mms-api.service'
import { normalizeErrorResponse } from 'utils/form'
import AlertToastr from 'utils/alert'
import useTranslation from 'Hooks/useTranslation'

const MarkAsInProgressModal = ({
  isOpen,
  isOpenManager,
  activity,
  activityIds,
  onSuccess,
}) => {
  const translation = useTranslation()
  const [orderNumber, setOrderNumber] = useState('')
  const [errors, setErrors] = useState({})
  const [disableButtons, setDisabledButtons] = useState(false)
  
  const isMultiple = Array.isArray(activityIds) && activityIds.length > 0
  const isInProgress = activity?.inProgress === true

  useEffect(() => {
    if (isOpen) {
      if (isMultiple) {
        // For multiple activities, start with empty order number
        setOrderNumber('')
      } else if (activity) {
        if (isInProgress && activity.orderNumber) {
          setOrderNumber(activity.orderNumber)
        } else {
          setOrderNumber('')
        }
      }
    }
  }, [isOpen, activity, isInProgress, isMultiple])

  const handleClose = () => {
    isOpenManager(false)
    setOrderNumber('')
    setErrors({})
  }

  const onSave = () => {
    if (isMultiple) {
      // Multiple activities - use bulk update endpoint
      if (activityIds.length === 0) {
        AlertToastr.showErrorAlert('No activities selected')
        return
      }
      
      setDisabledButtons(true)
      
      MmsService.markActivitiesInProgress(activityIds, orderNumber.trim() || null)
        .then((response) => {
          if (response.data.isSuccessful) {
            setDisabledButtons(false)
            handleClose()
            AlertToastr.showAlert(translation.notificationsActivityUpdated || 'Activities updated successfully')
            if (onSuccess) {
              onSuccess(activityIds)
            }
          }
        })
        .catch((error) => {
          if (!error || !error.response || !error.response.data) {
            setDisabledButtons(false)
            return
          }
          const resErrors = normalizeErrorResponse(error.response.data)
          if (resErrors.errors) setErrors(resErrors.errors)
          setDisabledButtons(false)
          if (resErrors && resErrors.message)
            AlertToastr.showErrorAlert(resErrors.message)
          if (error.response.data.errors)
            error.response.data.errors
              .filter((x) => x.PropertyName === '')
              .map((x) => {
                AlertToastr.showErrorAlert(x.ErrorMessage)
                return x
              })
        })
    } else {
      // Single activity - use patch endpoint
      if (!activity || !activity.id) {
        AlertToastr.showErrorAlert('Activity is not selected')
        return
      }
      
      setDisabledButtons(true)
      
      const activityData = {
        id: activity.id,
        inProgress: !isInProgress,
        orderNumber: orderNumber.trim() || null,
      }

      MmsService.patchActivity(activityData)
        .then((response) => {
          if (response.data.isSuccessful) {
            setDisabledButtons(false)
            handleClose()
            AlertToastr.showAlert(translation.notificationsActivityUpdated || 'Activity updated successfully')
            if (onSuccess) {
              onSuccess(activity)
            }
          }
        })
        .catch((error) => {
          if (!error || !error.response || !error.response.data) {
            setDisabledButtons(false)
            return
          }
          const resErrors = normalizeErrorResponse(error.response.data)
          if (resErrors.errors) setErrors(resErrors.errors)
          setDisabledButtons(false)
          if (resErrors && resErrors.message)
            AlertToastr.showErrorAlert(resErrors.message)
          if (error.response.data.errors)
            error.response.data.errors
              .filter((x) => x.PropertyName === '')
              .map((x) => {
                AlertToastr.showErrorAlert(x.ErrorMessage)
                return x
              })
        })
    }
  }

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault()
          onSave()
        }
      }}
      className="respModal scrollContentModal autoHeight"
    >
      <div className="formContentHolder">
        <div className="modalTitleHolder">
          <h1 className="modalTitle text-center">
            {isMultiple 
              ? (translation.popoverMarkAsInProgressLabel || 'Mark as in progress')
              : (isInProgress 
                ? (translation.popoverUnmarkAsInProgressLabel || 'Unmark in progress')
                : (translation.popoverMarkAsInProgressLabel || 'Mark as in progress'))}
          </h1>
        </div>
        <div className="formContent">
          <div className="formContentBlock modalFieldLabelSettings">
            <Tooltip
              disableFocusListener
              disableTouchListener
              title={errors && errors.orderNumber ? errors.orderNumber.join('\n') : ''}
            >
              <TextField
                value={orderNumber}
                onChange={(e) => {
                  if (errors.orderNumber) errors.orderNumber = null
                  setOrderNumber(e.target.value.trimStart())
                }}
                error={errors && errors.orderNumber && errors.orderNumber.length > 0}
                variant="outlined"
                margin="normal"
                label="Order number"
                id="markAsInProgressModalOrderNumber"
                fullWidth
              />
            </Tooltip>
          </div>
        </div>
        <div className="btnHolder end fixedWidthBtn">
          <Button
            disabled={disableButtons}
            variant="outlined"
            fullWidth
            onClick={handleClose}
            id="markAsInProgressModalCancelBtn"
          >
            {translation.cancelButtonLabel}
          </Button>
          <Button
            disabled={disableButtons}
            variant="contained"
            color="primary"
            id="markAsInProgressModalSaveBtn"
            onClick={onSave}
            fullWidth
          >
            {translation.saveButtonLabel}
          </Button>
        </div>
      </div>
    </Dialog>
  )
}

export default MarkAsInProgressModal

